import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allTopics, getTopicArticles, getTopicById } from "@/utils/data";

export default function Topic() {
  const { topicId = "" } = useParams();
  const topic = getTopicById(topicId);

  usePageMeta(
    topic ? `${topic.title} - 自慰健康生活专题` : "专题未找到 - 自慰健康生活",
    topic ? topic.intro.slice(0, 130) : "未找到对应的专题内容。",
    topic ? `${topic.title},自慰,自我关怀,健康生活` : "自慰,健康生活",
  );

  if (!topic) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-20 text-center">
        <i className="ri-git-repository-line text-5xl text-foreground-300"></i>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-900">专题未找到</h1>
        <p className="mt-3 text-sm text-foreground-600">你访问的专题不存在，可能已被移除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const topicArticles = getTopicArticles(topic);
  const otherTopics = allTopics.filter((item) => item.id !== topic.id);

  return (
    <div className="w-full">
      {/* 专题头图 */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden bg-primary-950">
        <img
          src={topic.cover}
          alt={topic.title}
          title={`${topic.title} 自慰健康生活专题`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/55 to-primary-950/35"></div>
        <div className="relative z-10 w-full h-full flex items-end">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 pb-8 md:pb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs backdrop-blur mb-4">
              <i className="ri-git-repository-line"></i>
              专题合辑
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
              {topic.title}
            </h1>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: "专题" }, { label: topic.title }]} />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <p className="text-base md:text-lg text-foreground-800 leading-relaxed">{topic.intro}</p>
          </div>
          <div className="lg:col-span-1">
            <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-accent-800 mb-2">
                <i className="ri-lightbulb-line"></i>
                推荐理由
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed">{topic.reason}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {topic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-accent-100 text-accent-800">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 专题文章 */}
        <section className="mt-10 md:mt-12">
          <div className="flex items-center gap-2 mb-6">
            <i className="ri-article-line text-primary-600 text-xl"></i>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              本专题包含 {topicArticles.length} 篇资讯
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {topicArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>

        {/* 相关专题 */}
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关专题</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            {otherTopics.map((item) => (
              <Link
                key={item.id}
                to={`/topic/${item.id}`}
                className="group rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-300 transition-colors"
              >
                <div className="w-full h-36 overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} 自慰健康生活专题`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{item.intro}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}